import React, {
  FC, useCallback, useMemo, useState,
} from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import { useModalsContext, useToggleModalsContext } from '../../contexts/ModalsContext';
import useMediaQuery from '../../hooks/useMediaQuery';
import { FormType, QuickQuoteInitialData } from '../QuickQuoteForm/types';
import Modal from '../Modal/Modal';
import grid from '../../../css/grid.module.scss';
import styles from './QuickQuoteModal.module.scss';

const QuickQuoteForm = loadable(
  () => import(/* webpackChunkName: 'quickQuoteForm' */'../QuickQuoteForm/QuickQuoteForm'),
);

interface QuickQuoteModalProps {
  quickQuote: QuickQuoteInitialData;
}

// eslint-disable-next-line
const globalAny: any = global; // TODO:  hack to add properties to Global object

const QuickQuoteModal: FC<QuickQuoteModalProps> = ({
  quickQuote,
}: QuickQuoteModalProps) => {
  const isMobile = useMediaQuery({ maxBreakpoint: 'xs' });
  const toggleModal = useToggleModalsContext();
  const { modalsState, properties } = useModalsContext();
  const { isQuickQuoteModalOpen: isOpen } = modalsState;

  const [isClosureTriggered, setIsClosureTriggered] = useState(false); // IO2702

  // TODO:  hack! remove after legacy pages migrate to React
  globalAny.toggleReactModal = toggleModal;
  // TODO: hack! remove after legacy pages migrate to React
  globalAny.modalsState = modalsState;

  const cancelClosure = useCallback(() => {
    setIsClosureTriggered(false);
  }, []);
  const getCloseEventName = (formType: FormType): string => {
    switch (formType) {
      case FormType.BookViewing:
        return 'book_a_tour_close';
      case FormType.GeneralEnquiry:
        return 'enquire_now_close';
      default:
        return 'quick_quote_close';
    }
  };


  const close = useCallback(() => {
    setIsClosureTriggered(false);
    toggleModal({ isQuickQuoteModalOpen: false });

    // ✅ Send close event to dataLayer
    const globalAny: any = global;
    const tracking = globalAny.quickQuoteTrackingData;
    const formType: FormType = tracking?.formType || FormType.QuickQuote;
    let stepNumber = 1;
    switch (tracking.form_step_label) {
      case 20:
        stepNumber = 1;
        break;
      case 40:
        stepNumber = 2;
        break;
      case 60:
        stepNumber = 3;
        break;
      case 80:
        stepNumber = 4;
        break;
      case 100:
        stepNumber = 5;
        break;
      default:
        stepNumber = 1;
    }
    if (tracking) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: getCloseEventName(formType),
        centre_id: tracking.cs_id.toString(),
        form_step_label: tracking.form_step_label || 'Unknown',
        form_step_number: stepNumber,
        listings_card_position: tracking.listingsCardPosition ?? null,
        listings_page_number: tracking.listingsPageNumber ?? null,
        popular: tracking.isPopular,
        rating_score: tracking.ratingScore,
      });
    }
  }, [toggleModal]);


  const titles = useMemo(() => ({
    [FormType.BookViewing]: quickQuote.bookAViewingLabel,
    [FormType.QuickQuote]: quickQuote.quickQuoteLabel,
    [FormType.LargeTeams]: quickQuote.quickQuoteLabel,
    [FormType.GeneralEnquiry]: quickQuote.quickQuoteLabel,
  }), [quickQuote.bookAViewingLabel, quickQuote.quickQuoteLabel]);

  if (!isOpen) {
    return null;
  }

  const type = properties.formType || FormType.QuickQuote;

  const title = (isMobile && properties?.title) || titles[type as keyof typeof titles];

  const containerClasses = {
    [styles.container]: true,
    [styles.isCompactOnMobile]: properties?.isCompactOnMobile,
  };

  return (
    <Modal
      classes={{
        overlay: classNames(grid.container, grid.containerFixed, styles.overlay),
        container: classNames(containerClasses),
        title: classNames(styles.title),
      }}
      closeHandler={close}
      isWithoutCloseButton={isClosureTriggered}
      title={title}
    >
      <QuickQuoteForm
        {...properties}
        cancelClosureCallback={cancelClosure}
        closeCallback={close}
        initialData={quickQuote}
        isClosureTriggered={isClosureTriggered}
        isModal
      />
    </Modal>
  );
};
QuickQuoteModal.displayName = 'QuickQuoteModal';

export default QuickQuoteModal;
